
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/auth";
import { gdprService } from "@/services/gdpr/GdprDeletionService";

// Map status from request record to frontend - updated to include all possible database statuses
type Status = "none" | "pending" | "cooling_off" | "ready" | "completed" | "cancelled" | "failed";

// Database status type to match what comes from the backend - including all possible statuses
type DatabaseStatus = "pending" | "cooling_off" | "processing" | "pending_auth_deletion" | "completed" | "cancelled" | "failed";

interface StatusResult {
  status: Status;
  coolingOffUntil: string | null;
  pendingEmail: string | null;
  hasDeletionRequest: boolean;
}

export function useSelfServiceDeletion() {
  const { user } = useAuth();
  const [statusResult, setStatusResult] = useState<StatusResult>({
    status: "none",
    coolingOffUntil: null,
    pendingEmail: null,
    hasDeletionRequest: false,
  });

  // Check if a deletion request exists for this user
  const fetchStatus = useCallback(async () => {
    if (!user?.id) {
      setStatusResult({
        status: "none",
        coolingOffUntil: null,
        pendingEmail: null,
        hasDeletionRequest: false,
      });
      return;
    }
    
    console.log('Fetching deletion status for user:', user.id);
    
    try {
      const requests = await gdprService.getDeletionRequests({
        userId: user.id
      });

      if (!requests || requests.length === 0) {
        console.log('No deletion request found');
        setStatusResult({
          status: "none",
          coolingOffUntil: null,
          pendingEmail: null,
          hasDeletionRequest: false,
        });
        return;
      }

      // Get the most recent non-cancelled and non-completed request
      const activeRequest = requests.find(req => !['cancelled', 'completed'].includes(req.status));
      
      // If no active request found, check if all are cancelled/completed
      if (!activeRequest) {
        console.log('All deletion requests are cancelled or completed');
        setStatusResult({
          status: "none",
          coolingOffUntil: null,
          pendingEmail: null,
          hasDeletionRequest: false,
        });
        return;
      }
      
      console.log('Found active deletion request:', activeRequest);

      // Map database status to frontend status
      let status: Status = "none";
      const dbStatus = activeRequest.status as DatabaseStatus;
      
      if (dbStatus === "pending") status = "pending";
      else if (dbStatus === "cooling_off") status = "cooling_off";
      else if (dbStatus === "processing" || dbStatus === "pending_auth_deletion") status = "ready";
      else if (dbStatus === "completed") status = "completed";
      else if (dbStatus === "cancelled") status = "cancelled";
      else if (dbStatus === "failed") status = "failed";

      // Extract cooling off period from the database column (not audit log)
      const coolingOffUntil = activeRequest.coolingOffUntil;
      const pendingEmail = activeRequest.email;

      console.log('Setting status result:', {
        status,
        coolingOffUntil,
        pendingEmail,
        hasDeletionRequest: true,
      });

      setStatusResult({
        status,
        coolingOffUntil,
        pendingEmail,
        hasDeletionRequest: true,
      });
    } catch (error: any) {
      console.error('Error fetching deletion status:', error);
      setStatusResult({
        status: "none",
        coolingOffUntil: null,
        pendingEmail: null,
        hasDeletionRequest: false,
      });
    }
  }, [user?.id]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const deleteRequest = useCallback(async () => {
    if (!user?.id || !user?.email) {
      throw new Error("User information not available");
    }
    
    console.log('Requesting deletion for user:', user.id);
    
    // Use the centralized GDPR service
    const result = await gdprService.requestDeletion(user.id, user.email, {
      enableNotifications: true,
      validateCompliance: true,
      cooldownPeriodDays: 7
    });
    
    console.log('Delete request response:', result);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create deletion request');
    }
    
    // Refresh status after successful request
    await fetchStatus();
  }, [user?.id, user?.email, fetchStatus]);

  return {
    ...statusResult,
    deleteRequest,
    refreshStatus: fetchStatus,
  };
}
